/*
 * Notifications (toasts) teqaConnect — fichier IDENTIQUE dans teqa_store et
 * teqa_confirmation (src/ui/feedback/toast.jsx). Modifier les deux ensemble.
 *
 *   const toast = useToast();
 *   toast.success("Commande créée");
 *   toast.error(err, "Impossible d'enregistrer");   // err axios -> message lisible
 *   const id = toast.loading("Envoi…"); toast.update(id, { type: "success", message: "Envoyé" });
 *   await toast.promise(api(), { loading: "Envoi…", success: "Envoyé", error: "Échec" });
 *
 * Messages groupés : un message identique déjà affiché n'est pas empilé, son
 * compteur augmente (×3) et son délai repart. Survol = pause.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { AlertTriangle, CheckCircle2, Info, Loader2, X, XCircle } from "lucide-react";

const ToastContext = createContext(null);

const DURATIONS = { success: 3500, info: 4500, warning: 5500, error: 7000, loading: 0 };

const TITLES = {
  success: "C'est fait",
  error: "Une erreur est survenue",
  warning: "Attention",
  info: "Information",
  loading: "Chargement…",
};

const TONES = {
  success: { icon: CheckCircle2, ring: "text-emerald-600 dark:text-emerald-400", bar: "bg-emerald-500" },
  error: { icon: XCircle, ring: "text-rose-600 dark:text-rose-400", bar: "bg-rose-500" },
  warning: { icon: AlertTriangle, ring: "text-amber-600 dark:text-amber-400", bar: "bg-amber-500" },
  info: { icon: Info, ring: "text-sky-600 dark:text-sky-400", bar: "bg-sky-500" },
  loading: { icon: Loader2, ring: "text-indigo-600 dark:text-indigo-400", bar: "bg-indigo-500" },
};

const KEYFRAMES = "@keyframes teqa-toast-progress{from{transform:scaleX(1)}to{transform:scaleX(0)}}";

const newId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

// Message lisible par défaut pour une erreur axios / JS
function defaultFormatError(error, fallback) {
  const data = error?.response?.data;
  if (typeof data === "string" && data.length < 300) return data;
  return data?.detail || data?.message || data?.non_field_errors?.[0] || fallback || error?.message || TITLES.error;
}

const isErrorLike = (value) => value instanceof Error || (value && typeof value === "object" && ("response" in value || "isAxiosError" in value));

/**
 * @param formatError (error, fallback) => string — traducteur d'erreurs de l'app
 *        (store : getApiErrorMessage ; confirmation : getFriendlyErrorMessage)
 */
export function ToastProvider({ children, formatError = defaultFormatError, max = 4 }) {
  const [toasts, setToasts] = useState([]);
  const timers = useRef(new Map()); // id -> { handle, remaining, startedAt }
  const toastsRef = useRef(toasts);
  toastsRef.current = toasts;

  const clearTimer = useCallback((id) => {
    const t = timers.current.get(id);
    if (t?.handle) clearTimeout(t.handle);
    timers.current.delete(id);
  }, []);

  const dismiss = useCallback(
    (id) => {
      if (id === undefined) {
        timers.current.forEach((_, key) => clearTimer(key));
        setToasts([]);
        return;
      }
      clearTimer(id);
      setToasts((list) => list.filter((t) => t.id !== id));
    },
    [clearTimer]
  );

  const startTimer = useCallback(
    (id, duration) => {
      clearTimer(id);
      if (!duration) return;
      const handle = setTimeout(() => dismiss(id), duration);
      timers.current.set(id, { handle, remaining: duration, startedAt: Date.now() });
    },
    [clearTimer, dismiss]
  );

  const pause = useCallback((id) => {
    const t = timers.current.get(id);
    if (!t?.handle) return;
    clearTimeout(t.handle);
    timers.current.set(id, { handle: null, remaining: Math.max(800, t.remaining - (Date.now() - t.startedAt)), startedAt: 0 });
    setToasts((list) => list.map((x) => (x.id === id ? { ...x, paused: true } : x)));
  }, []);

  const resume = useCallback(
    (id) => {
      const t = timers.current.get(id);
      if (!t || t.handle) return;
      const handle = setTimeout(() => dismiss(id), t.remaining);
      timers.current.set(id, { handle, remaining: t.remaining, startedAt: Date.now() });
      setToasts((list) => list.map((x) => (x.id === id ? { ...x, paused: false } : x)));
    },
    [dismiss]
  );

  const show = useCallback(
    ({ type = "info", title, message, duration, id, dedupe = true } = {}) => {
      const resolvedTitle = title || (message ? null : TITLES[type]);
      const ms = duration ?? DURATIONS[type] ?? 4500;
      const key = `${type}|${resolvedTitle || ""}|${message || ""}`;

      // mise à jour d'un toast existant (loading -> success…)
      if (id && toastsRef.current.some((t) => t.id === id)) {
        setToasts((list) =>
          list.map((t) => (t.id === id ? { ...t, type, title: resolvedTitle, message, key, duration: ms, count: 1, version: t.version + 1 } : t))
        );
        startTimer(id, ms);
        return id;
      }

      // regroupement : même message déjà visible -> compteur + délai relancé
      const twin = dedupe && type !== "loading" ? toastsRef.current.find((t) => t.key === key) : null;
      if (twin) {
        setToasts((list) => list.map((t) => (t.id === twin.id ? { ...t, count: t.count + 1, version: t.version + 1 } : t)));
        startTimer(twin.id, ms);
        return twin.id;
      }

      const toastId = id || newId();
      setToasts((list) => {
        const next = [{ id: toastId, type, title: resolvedTitle, message, key, duration: ms, count: 1, version: 0 }, ...list];
        next.slice(max).forEach((t) => clearTimer(t.id));
        return next.slice(0, max);
      });
      startTimer(toastId, ms);
      return toastId;
    },
    [clearTimer, max, startTimer]
  );

  useEffect(() => {
    const map = timers.current;
    return () => map.forEach((t) => t.handle && clearTimeout(t.handle));
  }, []);

  const api = useMemo(() => {
    const toMessage = (value, fallback) => (isErrorLike(value) ? formatError(value, fallback) : value);
    return {
      show,
      dismiss,
      success: (message, title) => show({ type: "success", message, title }),
      info: (message, title) => show({ type: "info", message, title }),
      warning: (message, title) => show({ type: "warning", message, title }),
      // toast.error(err) | toast.error(err, "Repli si l'erreur est muette") | toast.error("Texte", "Titre")
      error: (value, second) =>
        isErrorLike(value)
          ? show({ type: "error", message: toMessage(value, second) })
          : show({ type: "error", message: value, title: second }),
      loading: (message = TITLES.loading) => show({ type: "loading", message, dedupe: false }),
      update: (id, { type = "success", message, title, duration } = {}) => show({ id, type, message, title, duration }),
      promise: async (promise, { loading = TITLES.loading, success = TITLES.success, error = TITLES.error } = {}) => {
        const id = show({ type: "loading", message: loading, dedupe: false });
        try {
          const result = await (typeof promise === "function" ? promise() : promise);
          show({ id, type: "success", message: typeof success === "function" ? success(result) : success });
          return result;
        } catch (err) {
          show({ id, type: "error", message: typeof error === "function" ? error(err) : formatError(err, error) });
          throw err;
        }
      },
      // compatibilité : anciens appels showToast({...}) / push({...})
      showToast: (opts) => show(opts),
      push: (opts) => show({ type: "success", ...opts }),
    };
  }, [dismiss, formatError, show]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <style>{KEYFRAMES}</style>
      <div
        className="pointer-events-none fixed inset-x-3 top-3 z-[10000] flex flex-col items-center gap-2 sm:inset-x-auto sm:right-4 sm:top-4 sm:w-[380px] sm:items-stretch"
        aria-live="polite"
        aria-relevant="additions text"
      >
        {toasts.length > 1 ? (
          <button
            type="button"
            onClick={() => dismiss()}
            className="pointer-events-auto self-end rounded-full bg-slate-900/80 px-2.5 py-1 text-[11px] font-medium text-white backdrop-blur transition hover:bg-slate-900 dark:bg-slate-700/90"
          >
            Tout fermer ({toasts.length})
          </button>
        ) : null}
        <AnimatePresence initial={false}>
          {toasts.map((t) => (
            <ToastCard key={t.id} toast={t} onClose={() => dismiss(t.id)} onPause={() => pause(t.id)} onResume={() => resume(t.id)} />
          ))}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
}

function ToastCard({ toast, onClose, onPause, onResume }) {
  const tone = TONES[toast.type] || TONES.info;
  const Icon = tone.icon;
  const heading = toast.title || toast.message;
  const body = toast.title ? toast.message : null;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: -12, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, x: 24, scale: 0.97, transition: { duration: 0.15 } }}
      transition={{ type: "spring", stiffness: 420, damping: 32 }}
      onMouseEnter={onPause}
      onMouseLeave={onResume}
      role={toast.type === "error" ? "alert" : "status"}
      className="pointer-events-auto relative w-full max-w-[420px] overflow-hidden rounded-xl border border-slate-200 bg-white/95 shadow-lg shadow-slate-900/10 backdrop-blur dark:border-slate-700 dark:bg-slate-900/95"
    >
      <div className="flex items-start gap-3 px-4 py-3">
        <span className={`mt-0.5 shrink-0 ${tone.ring}`}>
          <Icon className={`h-5 w-5 ${toast.type === "loading" ? "animate-spin" : ""}`} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-slate-100">
            <span className="min-w-0 break-words">{heading}</span>
            {toast.count > 1 ? (
              <span className="shrink-0 rounded-full bg-slate-100 px-1.5 text-[11px] font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                ×{toast.count}
              </span>
            ) : null}
          </p>
          {body ? <p className="mt-0.5 break-words text-[13px] leading-snug text-slate-600 dark:text-slate-400">{body}</p> : null}
        </div>
        {toast.type !== "loading" ? (
          <button
            type="button"
            onClick={onClose}
            className="-mr-1 shrink-0 rounded-md p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200"
            aria-label="Fermer la notification"
          >
            <X className="h-4 w-4" />
          </button>
        ) : null}
      </div>
      {toast.duration ? (
        <span
          key={toast.version}
          className={`absolute bottom-0 left-0 h-0.5 w-full origin-left ${tone.bar} opacity-70`}
          style={{
            animation: `teqa-toast-progress ${toast.duration}ms linear forwards`,
            animationPlayState: toast.paused ? "paused" : "running",
          }}
        />
      ) : null}
    </motion.div>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast doit être utilisé dans <ToastProvider>");
  return ctx;
}

export default ToastProvider;
